import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Button,
  Avatar,
  Grid,
  CircularProgress,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import AdminDashboardLayout from "../layout/AdminDashboardLayout";
import { getAllProfiles } from "../../../services/adminService";

const ProfilesPage = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchMode, setSearchMode] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50); // Fixed at 50 per API
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalProfiles: 0,
    hasNextPage: false,
    hasPreviousPage: false
  });
  const [showSearchBar, setShowSearchBar] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const navigate = useNavigate();

  const fetchProfiles = async (pageNumber = 1, search = "", mode = "all") => {
    setLoading(true);
    try {
      const response = await getAllProfiles({
        page: pageNumber,
        search: search,
        searchMode: mode
      });
      
      // Handle new API response structure
      if (response && response.data) {
        setProfiles(response.data || []);
        const currentPage = response.pagination?.currentPage || pageNumber;
        setPagination({
          currentPage: currentPage,
          totalPages: response.pagination?.totalPages || 1,
          totalProfiles: response.pagination?.totalProfiles || 0,
          hasNextPage: response.pagination?.hasNextPage || false,
          hasPreviousPage: response.pagination?.hasPreviousPage || false
        });
        // Sync MUI pagination (0-indexed) with API pagination (1-indexed)
        setPage(currentPage - 1);
      } else if (Array.isArray(response)) {
        // Fallback for old API format
        setProfiles(response || []);
        setPagination({
          currentPage: pageNumber,
          totalPages: 1,
          totalProfiles: response.length || 0,
          hasNextPage: false,
          hasPreviousPage: false
        });
        setPage(pageNumber - 1);
      } else {
        setProfiles([]);
        setPagination({
          currentPage: 1,
          totalPages: 1,
          totalProfiles: 0,
          hasNextPage: false,
          hasPreviousPage: false
        });
        setPage(0);
      }
    } catch (err) {
      console.error("Failed to fetch profiles:", err);
      setProfiles([]);
      setPagination({
        currentPage: 1,
        totalPages: 1,
        totalProfiles: 0,
        hasNextPage: false,
        hasPreviousPage: false
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles(1, searchQuery, searchMode);
  }, []);

  const handleSearch = () => {
    setPage(0);
    fetchProfiles(1, searchQuery, searchMode);
  };

  const handleReset = () => {
    setSearchQuery("");
    setSearchMode("all");
    setPage(0);
    fetchProfiles(1, "", "all");
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    const serverPage = newPage + 1; // MUI pagination is 0-indexed, API is 1-indexed
    fetchProfiles(serverPage, searchQuery, searchMode);
  };

  const handleRowClick = (profileId) => {
    navigate(`/admin/profiledetails/${profileId}`);
  };

  return (
    <AdminDashboardLayout>
      <Box sx={{ px: 3, pt: 2, pb: 12 }}>
        <Typography variant="h4" fontWeight="bold" mb={2}>
          All Profiles
        </Typography>

        {/* Search Bar */}
        {showSearchBar && (
          <Paper
            elevation={3}
            sx={{
              mb: 4,
              p: expanded ? 3 : 1,
              borderRadius: 3,
              backgroundColor: "#fdfdfd",
              transition: "all 0.3s ease-in-out"
            }}
          >
            <Grid container alignItems="center" justifyContent="space-between" mb={expanded ? 2 : 0}>
              <Grid item>
                <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
                  Search Profiles
                </Typography>
              </Grid>
              <Grid item>
                <IconButton
                  onClick={() => setExpanded(!expanded)}
                  size="small"
                  sx={{ mr: 1 }}
                  title={expanded ? "Minimize" : "Expand"}
                >
                  {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </IconButton>
                <IconButton
                  onClick={() => setShowSearchBar(false)}
                  size="small"
                  title="Close Search"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Grid>
            </Grid>

            {expanded && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label="Search"
                    placeholder="Enter search query..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Search Mode</InputLabel>
                    <Select
                      value={searchMode}
                      onChange={(e) => setSearchMode(e.target.value)}
                      label="Search Mode"
                    >
                      <MenuItem value="all">All Fields</MenuItem>
                      <MenuItem value="name">Name</MenuItem>
                      <MenuItem value="email">Email</MenuItem>
                      <MenuItem value="mobile">Mobile</MenuItem>
                      <MenuItem value="company">Company</MenuItem>
                      <MenuItem value="linkedin">LinkedIn</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sx={{ textAlign: "right", mt: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    sx={{ mr: 2, borderRadius: 2, px: 3 }}
                  >
                    Search
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={handleReset}
                    sx={{ borderRadius: 2, px: 3 }}
                  >
                    Reset
                  </Button>
                </Grid>
              </Grid>
            )}
          </Paper>
        )}

        {/* Show Bar Button */}
        {!showSearchBar && (
          <Box mb={2}>
            <Button variant="outlined" onClick={() => setShowSearchBar(true)}>
              Show Search Bar
            </Button>
          </Box>
        )}

        {/* Table Section */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                <TableRow>
                  <TableCell><b>Profile</b></TableCell>
                  <TableCell><b>Name</b></TableCell>
                  <TableCell><b>Experience</b></TableCell>
                  {/* <TableCell><b>Mobile</b></TableCell> */}
                  <TableCell><b>Company</b></TableCell>
                  <TableCell><b>Has Details</b></TableCell>
                  <TableCell><b>Contacts Fetched</b></TableCell>
                  <TableCell><b>LinkedIn</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profiles.map((profile) => (
                    <TableRow
                      key={profile._id}
                      hover
                      onClick={() => handleRowClick(profile._id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>
                        <Avatar src={profile.profilePicture} alt={profile.firstName} />
                      </TableCell>
                      <TableCell>{profile.firstName} {profile.lastName}</TableCell>
                      <TableCell>{profile.totalExperience || "N/A"}</TableCell>
                      {/* <TableCell>{profile.phoneNos?.[0] || "N/A"}</TableCell> */}
                      <TableCell>{profile.currentOrg || "N/A"}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: profile.hasDetails ? '#4caf50' : '#f44336',
                            display: 'inline-block'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            backgroundColor: profile.hasInsights ? '#4caf50' : '#f44336',
                            display: 'inline-block'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        {profile.linkedinLink ? (
                          <a href={profile.linkedinLink} target="_blank" rel="noreferrer">
                            View
                          </a>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={pagination.totalProfiles}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[50]} // Fixed at 50 per API
              labelRowsPerPage=""
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
            />
          </TableContainer>
        )}
      </Box>
    </AdminDashboardLayout>
  );
};

export default ProfilesPage;